// Recert diff engine — compares the current wizard draft against the scrubbed
// snapshot we seeded from via Copy-from-last-visit. Surfaces a concise
// "changed since last visit" summary on the Step 9 verify screen so nurses
// see exactly what's new rather than eyeballing every field.
//
// Only diffs fields the nurse typically UPDATES on a recert. Visit-specific
// fields (assessment_date, cert_period, signatures, vitals) are EXCLUDED
// because they're cleared server-side and always appear "changed" which
// would be noise.

// Keys whose values we surface as top-level field changes.
const FIELD_SECTIONS = [
  ["client.first_name",           "First name"],
  ["client.last_name",            "Last name"],
  ["client.address_street",       "Address"],
  ["client.phone_primary",        "Phone"],
  ["client.forwardhealth_member_id", "Member ID"],
  ["physician.name",              "Physician"],
  ["physician.phone",             "Physician phone"],
  ["physician.npi",               "Physician NPI"],
  ["medical_history.past_history","Past history"],
  ["medical_history.chief_complaint","Chief complaint"],
  ["medical_history.drug_allergies","Drug allergies"],
  ["clinical_summary.rationale",  "Clinical rationale"],
  ["clinical_summary.weekly_hours","Weekly hours"],
  ["cms485.dme_supplies",         "DME / supplies"],
  ["cms485.orders_treatments",    "Orders & treatments"],
  ["cms485.goals_discharge",      "Goals / discharge plan"],
  ["social.lives_with",           "Lives with"],
  ["social.caregiver_support",    "Caregiver support"],
];

// Array sections we summarize as "+X / -Y / ~Z" per-entity counts.
const ARRAY_SECTIONS = [
  { key: "diagnoses",   label: "diagnoses",   identity: (x) => `${x.icd10 || ""}::${(x.description || "").trim().toLowerCase()}` },
  { key: "medications", label: "medications", identity: (x) => (x.name || "").trim().toLowerCase() },
];

// ADL levels — nurses frequently adjust these on recerts.
const ADL_KEYS = ["bathing", "dressing", "grooming", "mobility", "toileting", "eating", "continence"];

// Fields the wizard stores as free-text narrative. On a recert visit these
// should almost always get refreshed — if they're identical to last visit,
// flag them so the nurse can confirm they re-reviewed (not rubber-stamped).
const STALE_CANDIDATE_FIELDS = [
  ["clinical_summary.rationale",     "Clinical rationale"],
  ["clinical_summary.weekly_hours",  "Weekly hours"],
  ["clinical_summary.reassessment_plan","Reassessment plan"],
  ["cms485.dme_supplies",            "DME / supplies"],
  ["cms485.orders_treatments",       "Orders & treatments"],
  ["cms485.goals_discharge",         "Goals / discharge plan"],
  ["medical_history.past_history",   "Past medical history"],
  ["medical_history.chief_complaint","Chief complaint"],
  ["medical_history.drug_allergies", "Drug allergies"],
  ["f11136_other_info",              "Other information"],
];

/** Returns `[ { path, label }, … ]` for candidate fields that exist in both
 * payloads, are non-empty, and have IDENTICAL values. Empty/unfilled fields
 * don't appear (they belong in the Step 9 checklist, not here).
 */
export function staleFieldsOnRecert(current, snapshot) {
  if (!current || !snapshot) return [];
  const out = [];
  for (const [path, label] of STALE_CANDIDATE_FIELDS) {
    const before = normalizeBlank(getAt(snapshot, path));
    const after = normalizeBlank(getAt(current, path));
    if (!before || !after) continue;       // empty → skip (already-blank isn't "stale")
    if (String(before) !== String(after)) continue;  // changed → not stale
    if (String(after).length < 5) continue;  // too short to meaningfully "refresh"
    out.push({ path, label, sample: truncate(after, 70) });
  }
  return out;
}

function getAt(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function truncate(s, n = 40) {
  const t = String(s ?? "");
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
}

function normalizeBlank(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  return v;
}

/**
 * Compute a lightweight diff structure:
 * {
 *   fields: [{ path, label, before, after }],
 *   diagnoses: { added: [dx], removed: [dx] },
 *   medications: { added: [m], removed: [m], changed: [{before, after}] },
 *   adls: [{ key, before, after }],
 *   totalChanges: number,
 * }
 */
export function diffAgainstSnapshot(current, snapshot) {
  if (!current || !snapshot) {
    return { fields: [], diagnoses: { added: [], removed: [] }, medications: { added: [], removed: [], changed: [] }, adls: [], totalChanges: 0 };
  }

  const fields = [];
  for (const [path, label] of FIELD_SECTIONS) {
    const a = normalizeBlank(getAt(snapshot, path));
    const b = normalizeBlank(getAt(current, path));
    if (a !== b) fields.push({ path, label, before: a, after: b });
  }

  // Array diffs via identity function
  const arrayDiffs = {};
  for (const { key, identity } of ARRAY_SECTIONS) {
    const before = Array.isArray(snapshot[key]) ? snapshot[key].filter((x) => x && identity(x)) : [];
    const after = Array.isArray(current[key]) ? current[key].filter((x) => x && identity(x)) : [];
    const beforeIds = new Set(before.map(identity));
    const afterIds = new Set(after.map(identity));
    arrayDiffs[key] = {
      added:   after.filter((x) => !beforeIds.has(identity(x))),
      removed: before.filter((x) => !afterIds.has(identity(x))),
    };
  }
  // Medications additionally report "changed" when dose/frequency/route differ for the same name.
  const medChanged = [];
  {
    const byName = new Map();
    for (const m of (snapshot.medications || [])) {
      if (m?.name) byName.set(m.name.trim().toLowerCase(), m);
    }
    for (const m of (current.medications || [])) {
      const k = (m?.name || "").trim().toLowerCase();
      const prev = byName.get(k);
      if (prev) {
        const diff =
          (normalizeBlank(prev.dose) !== normalizeBlank(m.dose)) ||
          (normalizeBlank(prev.frequency) !== normalizeBlank(m.frequency)) ||
          (normalizeBlank(prev.route) !== normalizeBlank(m.route));
        if (diff) medChanged.push({ before: prev, after: m });
      }
    }
  }

  // ADL level changes (only flag the level field; comment churn is noisy).
  const adls = [];
  for (const k of ADL_KEYS) {
    const a = normalizeBlank(getAt(snapshot, `adl.${k}.pcst_level`));
    const b = normalizeBlank(getAt(current, `adl.${k}.pcst_level`));
    if (a !== b) adls.push({ key: k, before: a, after: b });
  }

  const totalChanges =
    fields.length +
    (arrayDiffs.diagnoses?.added.length || 0) + (arrayDiffs.diagnoses?.removed.length || 0) +
    (arrayDiffs.medications?.added.length || 0) + (arrayDiffs.medications?.removed.length || 0) +
    medChanged.length + adls.length;

  return {
    fields,
    diagnoses: arrayDiffs.diagnoses || { added: [], removed: [] },
    medications: { ...(arrayDiffs.medications || { added: [], removed: [] }), changed: medChanged },
    adls,
    totalChanges,
    truncate,
  };
}
